import { CardOptionContent } from '@framework/form/inputs/card-group/CardOptionContent.tsx';
import { type IRadioCardOption } from '@framework/form/inputs/card-group/RadioCardGroupInput.tsx';
import { TbIcon } from '@framework/ui/components/icon/TbIcon.tsx';
import { yup } from '@framework/validation/initYup.ts';
import { IconCircleDot, IconSelect } from '@tabler/icons-react';
import { useWatch } from 'react-hook-form';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { WidgetType } from '../../../Widget.ts';
import { BaseLayoutSchema } from '../../common/BaseLayoutSchema.ts';
import { WidgetConfigurationSlotRender } from '../../common/config-slots/WidgetConfigurationSlotRender.tsx';
import { WidgetConfigurationSlots } from '../../common/config-slots/WidgetConfigurationSlots.tsx';
import {
  CommonConfigComponent,
  CommonInputWidgetConfigSchema,
} from '../common/CommonInputConfigComponent.tsx';
import { CommonKindInputConfig } from '../common/CommonKindInputConfig.tsx';
import {
  HideLabelConfigInput,
  hideLabelConfigSchema,
} from '../common/HideLabelConfigInput.tsx';
import { LabelLayoutConfigInput } from '../common/label-layout/LabelLayoutConfigInput.tsx';
import {
  CommonOptionListConfigComponent,
  commonOptionListDirectionValidator,
  commonOptionListSeparatorValidator,
} from '../common/option-list/CommonOptionListConfigComponent.tsx';
import { SetOptionsFromVariableInput } from '../common/option-list/SetOptionsFromVariableInput.tsx';
import { widgetOptionArrayValidator } from '../common/option-list/validation/optionArrayValidator.ts';
import { WidgetOptionItemList } from '../common/option-list/WidgetOptionItemList.tsx';
import {
  type ISingleSelectWidget,
  SingleSelectKind,
} from './ISingleSelectWidget.ts';

export const singleSelectInputConfigValidator: yup.ObjectSchema<ISingleSelectWidget> =
  CommonInputWidgetConfigSchema.shape({
    type: yup.string().exact(WidgetType.Select).required(),
    options: widgetOptionArrayValidator,
    kind: yup
      .string()
      .oneOfEnum(SingleSelectKind)
      .default(SingleSelectKind.Radio)
      .required()
      .label('Tipo di campo'),
    layout: BaseLayoutSchema.shape({
      hideLabel: hideLabelConfigSchema,
    })
      // Opzioni solo per checkbox
      .when('kind', ([kind]: SingleSelectKind[], schema) => {
        if (kind === SingleSelectKind.Radio) {
          return schema.shape({
            direction: commonOptionListDirectionValidator,
            separator: commonOptionListSeparatorValidator,
          });
        }
        return schema;
      })
      .required()
      .noUnknown(),
  })
    .required()
    .noUnknown();

const kindOptions: IRadioCardOption[] = [
  {
    value: SingleSelectKind.Select,
    label: (
      <CardOptionContent
        icon={<TbIcon icon={IconSelect} />}
        title="Menù a tendina"
        description="Apre un elenco a comparsa; adatto a molte opzioni."
      />
    ),
  },
  {
    value: SingleSelectKind.Radio,
    label: (
      <CardOptionContent
        icon={<TbIcon icon={IconCircleDot} />}
        title="Lista di radio"
        description="Tutte le opzioni sempre visibili, scelta singola."
      />
    ),
  },
];

export interface ISingleSelectConfigComponentProps
  extends IWidgetComponentProps<ISingleSelectWidget> {}

export function SingleSelectConfigComponent(
  props: ISingleSelectConfigComponentProps,
) {
  // useWatch (non getValue): il valore decide la registrazione dei fill,
  // serve la sottoscrizione al campo per aggiornare le card al cambio.
  const kind = useWatch({ name: 'kind' }) as SingleSelectKind;

  return (
    <>
      <CommonKindInputConfig kindOptions={kindOptions} />
      {/* Configurazioni comuni */}
      <CommonConfigComponent
        {...props}
        hideConfigPlaceholder={kind === SingleSelectKind.Radio}
      />

      {/* Disposizione etichetta, orientamento e separatori: solo radio */}
      {kind === SingleSelectKind.Radio && (
        <WidgetConfigurationSlotRender
          slot={WidgetConfigurationSlots.Layout}
          order={20}
        >
          <LabelLayoutConfigInput />
          <CommonOptionListConfigComponent />
        </WidgetConfigurationSlotRender>
      )}
      <WidgetConfigurationSlotRender
        slot={WidgetConfigurationSlots.Layout}
        order={30}
      >
        <HideLabelConfigInput />
      </WidgetConfigurationSlotRender>
      <WidgetConfigurationSlotRender slot={WidgetConfigurationSlots.Options}>
        <WidgetOptionItemList name="options" />
        <SetOptionsFromVariableInput name="options" />
      </WidgetConfigurationSlotRender>
    </>
  );
}
